import React, { createRef, useState } from 'react';
import Modal from '../Modal/Modal';
import styles from './InstagramShareModal.scss';
import JgButtonGroup from '@justgiving/button-group';
import { InstagramIcon } from '../SocialIcons/InstagramIcon';
import { useApp, useBasePageQuery } from '../../hooks';
import { filterByTypename } from '../../utils/graphql';
import { useScreenshot } from '../../hooks/useScreenshot';
import {
  IG_SHARE_TYPE,
  InstagramShare,
} from '../InstagramShare/InstagramShare';
import { useTranslation } from 'react-i18next';
import Button from '../Button/Button';
import { InstagramShareScreenshot } from '../InstagramShareScreenshot/InstagramShareScreenshot';
import { useShareContext } from '../../context/ShareContext';

export interface InstagramShareModalProps {
  onClose: () => void;
}

export const InstagramShareModal: React.FunctionComponent<InstagramShareModalProps> = ({
  onClose,
}) => {
  const { t } = useTranslation('common', { useSuspense: false });
  const ref = createRef<HTMLDivElement>();
  const { shareItems } = useShareContext();
  const { takeInstagramScreenshot } = useScreenshot({
    quality: 1,
    pixelRatio: 2,
  });

  const [selectedOption, setSelectedOption] = useState<string>(
    'Instagram Post',
  );

  const { showToast } = useApp();
  const { data: basePageData } = useBasePageQuery();

  if (!basePageData?.page) {
    return null;
  }

  const { hero, cover } = basePageData.page;

  let heroImageUrl = (hero?.imageUrl ||
    (cover?.__typename === 'ImageMedia' && cover?.url)) as string;
  let altText =
    hero?.imageTitle || (cover?.__typename === 'ImageMedia' && cover.alt) || '';

  const charity = basePageData?.page?.relationships?.beneficiaries?.nodes
    ? filterByTypename(
        basePageData?.page?.relationships?.beneficiaries?.nodes,
        'Charity',
      )
    : null;
  const charityName = charity?.[0]?.name ?? 'charity';
  const pageUrl = shareItems.instagramShareModal.url;

  const postCopy = `${t('shareModal.instagram.raisingFor', {
    charityName: charityName,
  })}
${t('shareModal.instagram.supportMeAt', {
  pageUrl: pageUrl,
})}`;

  const storyCopy = pageUrl;

  const takeScreenshot = async () => {
    const image = await takeInstagramScreenshot(ref.current!);

    image
      ? shareImage(image)
      : showToast({
          message: t('shareModal.instagram.errorWhenSharing'),
          type: 'warning',
          dismissAfter: 3000,
        });
  };

  const shareImage = async (image: Blob) => {
    const shareData = {
      files: [
        new File([image], 'instagramShare.jpg', {
          type: 'image/jpeg',
        }),
      ],
    } as ShareData;

    const navigator = window.navigator as any;

    if (navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        if (!error!.toString().includes('AbortError' || 'share canceled')) {
          showToast({
            message: t('shareModal.instagram.errorWhenSharing'),
            type: 'warning',
            dismissAfter: 3000,
          });
        }
      }
    } else {
      showToast({
        message: t('shareModal.instagram.unsupportedBrowser'),
        type: 'warning',
        dismissAfter: 3000,
      });
    }
  };

  return (
    <Modal
      className={styles.modal}
      displayCloseButton
      onCloseRequest={onClose}
      focusLock={false}
    >
      <div className={`row ${styles.topContainer}`}>
        <p className={styles.modalTitle}>
          {t('shareModal.instagram.chooseATemplate')}
        </p>
      </div>
      <JgButtonGroup
        onOptionClick={e => setSelectedOption(e.label)}
        options={[
          { label: 'Instagram Post', value: 'Instagram Post' },
          { label: 'Instagram Story', value: 'Instagram Story' },
        ]}
        selectedOption={selectedOption}
      />
      <InstagramShare
        heroImageUrl={heroImageUrl}
        altText={altText}
        copyText={selectedOption === 'Instagram Post' ? postCopy : storyCopy}
        type={
          selectedOption === 'Instagram Post'
            ? IG_SHARE_TYPE.POST
            : IG_SHARE_TYPE.STORY
        }
      />
      <InstagramShareScreenshot
        heroImageUrl={heroImageUrl}
        altText={altText}
        screenshotRef={ref}
        type={
          selectedOption === 'Instagram Post'
            ? IG_SHARE_TYPE.POST
            : IG_SHARE_TYPE.STORY
        }
      />
      <Button className={styles.shareButton} onClick={() => takeScreenshot()}>
        {<InstagramIcon width="24px" height="24px" color="#fff" />}{' '}
        {t('shareModal.instagram.share')}{' '}
      </Button>
    </Modal>
  );
};
